import { Clock, TrendingUp, Layers, Ruler, ChevronDown, ChevronRight } from "lucide-react";
import { formatEuro } from "@/lib/api";
import { useState } from "react";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatMin(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function estimateFabricationTimeMin(config, breakdown, isAssembly = false) {
  let min = 30; // base montage/réglages
  min += (config?.sections?.length || 1) * 10; // base par section

  (breakdown?.sections || []).forEach((sec) => {
    const leafCount = sec.door_specs?.leaf_count || 1;
    if (sec.has_door) min += leafCount >= 2 ? 25 : 15;
    min += (sec.drawers || 0) * 20;
    min += (sec.shelves_fixed || 0) * 8;
    min += (sec.shelves_mobile || 0) * 5;
    if (sec.has_penderie_bar) min += 10;
    if ((sec.mirrors || 0) > 0) min += 15;
  });

  if (config?.options?.has_back_panel) min += 10;
  if (config?.options?.under_slope_enabled) min += 30;
  if (config?.options?.plinth) min += 15;
  if (isAssembly) min += 45;

  return min;
}

export function calcSurfaces(config) {
  if (!config) return { jouesM2: 0, fondM2: 0, tablettesM2: 0 };
  const H = (config.height || 0) * 10; // cm → mm
  const W = (config.total_width || 0) * 10;
  const D = (config.depth || 0) * 10;
  const sections = config.sections || [];
  const nSections = sections.length || 1;

  // Joues + séparations internes = (n_sections + 1) panneaux * H * D
  const jouesM2 = ((nSections + 1) * (H / 1000) * (D / 1000));
  // Fond = W * H
  const fondM2 = (W / 1000) * (H / 1000);

  let tablettesM2 = 0;
  sections.forEach((sec) => {
    const secW = ((sec.width_cm || (config.total_width / nSections)) * 10) / 1000;
    const secD = D / 1000;
    const els = sec.elements || [];
    const nFixed = els.filter((e) => e.kind === "shelf_fixed").length || (sec.shelves_fixed || 0);
    const nMobile = els.filter((e) => e.kind === "shelf_mobile").length || (sec.shelves_mobile || 0);
    tablettesM2 += (nFixed + nMobile) * secW * secD;
  });

  return { jouesM2, fondM2, tablettesM2 };
}

// ─── SectionRow ──────────────────────────────────────────────────────────────

function SectionRow({ section, index, coeff, config }) {
  const [open, setOpen] = useState(false);
  const rawFactor = coeff > 0 ? 1 / coeff : 1;
  const secConfig = (config?.sections || [])[index] || {};
  const secWmm = Math.round((secConfig.width_cm || 0) * 10);

  const lines = [
    { label: "Structure (joue/dos/connecteurs)", val: section.section_base_price },
    { label: `Porte(s)`, val: section.door_price, skip: !section.has_door },
    { label: `Tiroirs (${section.drawers})`, val: section.drawers_price, skip: !section.drawers },
    { label: "Tablettes fixes", val: section.shelves_fixed_price, skip: !section.shelves_fixed },
    { label: "Tablettes mobiles", val: section.shelves_mobile_price, skip: !section.shelves_mobile },
    { label: "Penderie", val: section.penderie_price, skip: !section.has_penderie_bar },
    { label: "Miroir", val: section.mirror_price, skip: !section.mirrors },
    { label: "Poignées", val: section.handle_price, skip: !section.handle_price },
  ].filter((l) => !l.skip && l.val > 0);

  return (
    <div className="border border-border/50 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-background/60 hover:bg-background text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <span className="text-xs font-semibold">Section {section.id}{secWmm ? ` — ${secWmm} mm` : ""}</span>
          {section.has_door && <span className="text-[9px] bg-primary/10 text-primary rounded px-1.5 py-0.5">Porte</span>}
          {section.drawers > 0 && <span className="text-[9px] bg-accent/10 text-accent-foreground rounded px-1.5 py-0.5">{section.drawers} tiroir{section.drawers > 1 ? "s" : ""}</span>}
        </div>
        <div className="flex items-center gap-4 text-xs tabular-nums">
          <span className="text-muted-foreground">{formatEuro(section.subtotal * rawFactor)}</span>
          <span className="font-semibold">{formatEuro(section.subtotal)}</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-2 space-y-1 bg-background/30">
          <div className="grid grid-cols-3 gap-1 text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
            <span>Poste</span><span className="text-right">Brut HT</span><span className="text-right">Après coeff.</span>
          </div>
          {lines.map((l) => (
            <div key={l.label} className="grid grid-cols-3 gap-1 text-xs">
              <span className="text-muted-foreground truncate">{l.label}</span>
              <span className="text-right tabular-nums text-muted-foreground">{formatEuro(l.val * rawFactor)}</span>
              <span className="text-right tabular-nums font-medium">{formatEuro(l.val)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function AdminQuoteReport({ quote }) {
  if (!quote) return null;

  const coeff = quote.applied_price_coefficient || 1;
  const rawFactor = coeff > 0 ? 1 / coeff : 1;
  const coeffRole = quote.admin_price_coefficient !== 1 && quote.applied_price_coefficient === quote.admin_price_coefficient
    ? "admin"
    : "pro";

  const allItems = quote.items || [];
  const isMulti = allItems.length > 0;

  // Financial metrics
  const furnitureTotalHt = quote.furniture_total_ht || quote.total_ht_all_items || quote.breakdown?.total_ht || 0;
  const rawFurnitureTotal = furnitureTotalHt * rawFactor;
  const margeEuros = furnitureTotalHt - rawFurnitureTotal;
  const margePct = rawFurnitureTotal > 0 ? (margeEuros / rawFurnitureTotal) * 100 : 0;

  // Time estimation
  const isAssembly = !!quote.assembly;
  const mainTime = estimateFabricationTimeMin(quote.config, quote.breakdown, false);
  const itemsTime = allItems.reduce((sum, it) => sum + estimateFabricationTimeMin(it.config, it.breakdown, false), 0);
  const assemblyBonus = isAssembly ? 45 : 0;
  const totalTimeMin = mainTime + itemsTime + assemblyBonus;

  // Surfaces
  const mainSurfaces = calcSurfaces(quote.config);
  const itemsSurfaces = allItems.reduce(
    (acc, it) => {
      const s = calcSurfaces(it.config);
      return { jouesM2: acc.jouesM2 + s.jouesM2, fondM2: acc.fondM2 + s.fondM2, tablettesM2: acc.tablettesM2 + s.tablettesM2 };
    },
    { jouesM2: 0, fondM2: 0, tablettesM2: 0 }
  );
  const totalSurfaces = {
    jouesM2: mainSurfaces.jouesM2 + itemsSurfaces.jouesM2,
    fondM2: mainSurfaces.fondM2 + itemsSurfaces.fondM2,
    tablettesM2: mainSurfaces.tablettesM2 + itemsSurfaces.tablettesM2,
  };

  // Sections across all items for breakdown display
  const meubles = [
    { label: `Meuble ${allItems.length + 1} (principal)`, config: quote.config, breakdown: quote.breakdown },
    ...allItems.map((it, i) => ({ label: `Meuble ${i + 1}`, config: it.config, breakdown: it.breakdown })),
  ];

  return (
    <section
      data-testid="admin-quote-report"
      className="mt-8 border-2 border-dashed border-primary/30 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className="bg-foreground text-background px-5 py-3 flex items-center gap-2">
        <ShieldIcon className="w-4 h-4 shrink-0" />
        <div>
          <p className="text-xs font-bold tracking-wider uppercase">Fiche technique interne — Administration</p>
          <p className="text-[10px] opacity-60">Visible uniquement pour le compte administrateur · non incluse dans le PDF client</p>
        </div>
      </div>

      <div className="p-5 space-y-6 bg-card/30">

        {/* ── Métriques financières ── */}
        <div>
          <p className="eyebrow text-[10px] mb-3 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Métriques financières</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Prix de revient brut HT" value={formatEuro(rawFurnitureTotal)} sub="sans coefficient" />
            <MetricCard
              label="Coefficient appliqué"
              value={coeff.toFixed(4).replace(/\.?0+$/, "")}
              sub={coeffRole === "admin" ? "coefficient admin" : "coefficient pro (revente)"}
              highlight
            />
            <MetricCard label="Prix final HT fournitures" value={formatEuro(furnitureTotalHt)} sub="facturé au client" />
            <MetricCard
              label={`Marge brute estimée`}
              value={`${formatEuro(margeEuros)} (${margePct.toFixed(1)} %)`}
              sub={margePct >= 0 ? "marge positive" : "⚠ prix en dessous du brut"}
              accent={margePct >= 0}
            />
          </div>
        </div>

        {/* ── Temps fabrication ── */}
        <div>
          <p className="eyebrow text-[10px] mb-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Estimation temps fabrication</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {meubles.map((m, i) => {
              const t = estimateFabricationTimeMin(m.config, m.breakdown, false);
              return (
                <MetricCard key={i} label={m.label} value={formatMin(t)} sub={`${t} min estimées`} />
              );
            })}
            {isAssembly && <MetricCard label="Assemblage sur chantier" value="+45 min" sub="pose + calage modules" />}
            <MetricCard label="TOTAL PROJET" value={formatMin(totalTimeMin)} sub={`${totalTimeMin} min au total`} highlight />
          </div>
        </div>

        {/* ── Surfaces matière ── */}
        <div>
          <p className="eyebrow text-[10px] mb-3 flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Surfaces matière estimées</p>
          <div className="grid grid-cols-3 gap-3">
            <MetricCard label="Joues + Séparations" value={`${totalSurfaces.jouesM2.toFixed(2)} m²`} sub="panneaux latéraux" />
            <MetricCard label="Fond (dos)" value={`${totalSurfaces.fondM2.toFixed(2)} m²`} sub="fond mélaminé" />
            <MetricCard label="Tablettes" value={`${totalSurfaces.tablettesM2.toFixed(2)} m²`} sub="fixes + mobiles" />
          </div>
        </div>

        {/* ── Détail par section ── */}
        <div>
          <p className="eyebrow text-[10px] mb-3 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Détail par section — brut vs après coefficient</p>
          <div className="space-y-4">
            {meubles.map((m, mi) => (
              <div key={mi}>
                {isMulti && <p className="text-xs font-semibold text-muted-foreground mb-1.5">{m.label}</p>}
                <div className="space-y-1">
                  {(m.breakdown?.sections || []).map((sec, si) => (
                    <SectionRow key={sec.id || si} section={sec} index={si} coeff={coeff} config={m.config} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Coefficients stockés ── */}
        <div className="border border-border/50 rounded-lg px-4 py-3 bg-background/40 grid sm:grid-cols-3 gap-3 text-xs">
          <div>
            <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1">Coeff. admin (config admin)</p>
            <p className="font-mono font-semibold">{quote.admin_price_coefficient ?? 1}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1">Coeff. revente (config pro)</p>
            <p className="font-mono font-semibold">{quote.resale_price_coefficient ?? 1}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1">Coeff. appliqué (figé sur ce devis)</p>
            <p className="font-mono font-semibold text-primary">{quote.applied_price_coefficient ?? 1}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricCard({ label, value, sub, highlight, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "border-primary/40 bg-primary/5" : accent ? "border-green-500/30 bg-green-500/5" : "border-border bg-background/60"}`}>
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1 leading-tight">{label}</p>
      <p className={`font-heading text-sm font-bold tabular-nums ${highlight ? "text-primary" : accent ? "text-green-700 dark:text-green-400" : "text-foreground"}`}>{value}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5 leading-tight">{sub}</p>}
    </div>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
